import { getThemeColor } from 'theme/colors';

/**
 * Pastel chart color palette — derived from the strikeUp brand pastels.
 * First 4 are direct extensions of the 4 brand pastel keys (more saturated so they
 * show up clearly on white/light backgrounds). The remaining 6 fill the hue wheel
 * in the same muted, low-saturation register.
 *
 *  1. Sky Blue    — Pastel Blue family   (#DDECF0 → more saturated)
 *  2. Warm Honey  — Pastel Yellow family (#F5EFDA → more saturated)
 *  3. Sage Green  — Pastel Green family  (#E8E8DF → more saturated)
 *  4. Lavender    — Pastel Purple family (#EBEEFA → more saturated)
 *  5. Dusty Rose  — warm complement
 *  6. Seafoam     — blue-green transition
 *  7. Warm Peach  — warm amber
 *  8. Periwinkle  — cool blue
 *  9. Mint        — fresh green
 * 10. Dusty Mauve — pink-purple
 */
export const CHART_COLORS = [
  '#A8D4E0', // 1 — Sky Blue
  '#E8D090', // 2 — Warm Honey
  '#A8C498', // 3 — Sage Green
  '#B8B4E8', // 4 — Lavender
  '#E8A8A0', // 5 — Dusty Rose
  '#80C4B8', // 6 — Seafoam Teal
  '#E0B880', // 7 — Warm Peach
  '#98B4D8', // 8 — Periwinkle
  '#B8D8A0', // 9 — Light Mint
  '#D4A8C4', // 10 — Dusty Mauve
];

// Get chart theme based on color mode using Chakra color tokens
export const getChartTheme = (isDark: boolean) => ({
  background: 'transparent',
  text: {
    fontSize: 11,
    fill: `var(--chakra-colors-gray-${isDark ? '200' : '800'})`,
    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  axis: {
    domain: {
      line: {
        stroke: `var(--chakra-colors-gray-${isDark ? '600' : '300'})`,
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: `var(--chakra-colors-gray-${isDark ? '600' : '300'})`,
        strokeWidth: 1,
      },
      text: {
        fontSize: 11,
        fill: `var(--chakra-colors-gray-${isDark ? '300' : '700'})`,
      },
    },
    legend: {
      text: {
        fontSize: 12,
        fill: `var(--chakra-colors-gray-${isDark ? '200' : '800'})`,
        fontWeight: 600,
      },
    },
  },
  grid: {
    line: {
      stroke: `var(--chakra-colors-gray-${isDark ? '700' : '200'})`,
      strokeWidth: 1,
      strokeOpacity: isDark ? 0.3 : 0.2,
    },
  },
  legends: {
    text: {
      fontSize: 11,
      fill: `var(--chakra-colors-gray-${isDark ? '200' : '800'})`,
    },
  },
  tooltip: {
    container: {
      background: isDark ? '#28282B' : getThemeColor('bg.primary').base,
      color: isDark ? '#FFFFFF' : getThemeColor('text.primary').base,
      fontSize: 12,
      borderRadius: '6px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      padding: '8px 12px',
      border: `1px solid var(--chakra-colors-gray-${isDark ? '600' : '200'})`,
    },
  },
  labels: {
    text: {
      fontSize: 11,
      fill: `var(--chakra-colors-gray-800)`,
    },
  },
});

// Default theme for light mode (for backward compatibility)
export const CHART_THEME = getChartTheme(false);
